import type {
  KrsNode,
  KrsEdge,
  EdgeKind,
  DeployNode,
  OrganizationBlock,
  TeamNode,
} from "../types/ast.js";
import { INFRA_KIND_SET } from "../types/ast.js";
import { bareIdView, nodePathRefId } from "../parser/node-path.js";
import { UNASSIGNED_SYSTEM_ID } from "../view/unassigned-system.js";
import { hasShape } from "../shapes/shape-registry.js";
// `hasShape` answers for the built-in icons only once they are registered (#2802).
import "../shapes/builtin-icons.js";
import { CLIENT_SUBTYPE_TAGS, type ClientSubtypeTag } from "../builtins/icon-theme.js";
import { NON_AUTHOR_SHEET_IDS } from "../builtins/non-author-sheets.js";
import { flattenSheetsInCascadeOrder, mergeInCascadeOrder } from "../style/cascade.js";
import type {
  StyleSheet,
  StyleRule,
  StyleSelector,
  ResolvedNodeStyle,
  ResolvedEdgeStyle,
  ResolvedFrameStyle,
  ResolvedBoundaryFrames,
  ResolvedTeamFrames,
  PaintedNodeColors,
  ResolvedStyles,
  ResolvedLayoutHints,
  ResolvedStyleWarning,
  LayoutColumn,
  ShapeKind,
} from "../types/style.js";

const VALID_COLUMN_VALUES: ReadonlySet<string> = new Set<LayoutColumn>(["left", "center", "right"]);

const DEFAULT_NODE_STYLE: ResolvedNodeStyle = {
  backgroundColor: "#374151",
  color: "#F9FAFB",
  borderColor: "#4B5563",
  borderWidth: 2,
  borderStyle: "solid",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: "bold",
  fontFamily: "sans-serif",
  opacity: 1.0,
  shape: "box",
};

const DEFAULT_EDGE_STYLE: ResolvedEdgeStyle = {
  color: "#94A3B8",
  strokeWidth: 1.5,
  fontSize: 11,
  strokeStyle: "solid",
  direction: "auto",
  labelPosition: 0.5,
  labelOffsetX: 0,
  labelOffsetY: 0,
  // The canvas tier of edge-label disclosure (#3022). 48 clears every authored
  // label in `examples/` (the longest is 30), so the budget only bites on
  // labels that carry a sentence. Measured on real models, a smaller budget
  // truncates many labels to seat a few more, and a larger one makes a label
  // wider than a node card (about 6.6px a character against a 364px card).
  labelMaxChars: 48,
  labelDisplay: "auto",
};

const EDGE_DIRECTION_VALUES = new Set<string>(["auto", "up", "down", "left", "right"]);

const LABEL_POSITION_KEYWORDS: Record<string, number> = {
  start: 0,
  middle: 0.5,
  end: 1,
};

const SHAPE_KEYWORDS = new Set<string>(["box", "user", "cylinder", "queue", "hexagon", "cloud"]);

/** Maps infra sub-resource AST kind → the style tag used in resource[tag] selectors. */
export const INFRA_SUB_KIND_TO_TAG: Readonly<Record<string, string>> = {
  table: "table",
  "queue-item": "queue",
  bucket: "storage",
};

/**
 * Build a map from dot-notation resource IDs (e.g. "OrderDB.OrderTable") to the inferred
 * style tag derived from the referenced infra sub-resource kind. Only covers sub-resources
 * that are direct children of a system-level infra node.
 */
function buildInferredTagMap(systems: KrsNode[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const system of systems) {
    for (const node of system.children) {
      if (!INFRA_KIND_SET.has(node.kind)) continue;
      for (const sub of node.children) {
        const tag = INFRA_SUB_KIND_TO_TAG[sub.kind];
        if (tag) map.set(`${node.id}.${sub.id}`, tag);
      }
    }
  }
  return map;
}

export function resolveStyles(
  systems: KrsNode[],
  sheets: StyleSheet[],
  deployNodes?: DeployNode[],
  organizations?: OrganizationBlock[],
  extraNodes?: KrsNode[],
  extraEdges?: KrsEdge[],
  /**
   * `KrsFile.nodePathRanking`, which picks the bare-id entry where two logical
   * nodes share an id (#2934). Without it a shared id falls to path text order.
   */
  nodePathRanking?: ReadonlyMap<string, readonly (readonly string[])[]>,
  options: {
    /**
     * `false` leaves the `cyclic` virtual tag off every edge, so `edge[cyclic]`
     * matches nothing. A diff passes it: diff draws what changed, and the
     * cyclic red is the diff's "removed" red (#2791). Edge colour resolves from
     * the model edges here, so dropping the flag on the slice alone would not
     * keep the red out.
     */
    cyclicTag?: boolean;
  } = {},
): ResolvedStyles {
  const cyclicTag = options.cyclicTag ?? true;
  const allRules = flattenSheetsInCascadeOrder(sheets);
  const nodeStyles = new Map<string, ResolvedNodeStyle>();
  const edgeStyles = new Map<string, ResolvedEdgeStyle>();
  const layoutHints = new Map<string, ResolvedLayoutHints>();
  const paintedColors = new Map<string, PaintedNodeColors>();
  // The logical nodes' values per full path (#2934). The bare `nodeStyles` /
  // `layoutHints` / `paintedColors` entries for them are derived from these
  // once every logical node is processed, so a shared id resolves to one node
  // in all three instead of to whichever was processed last.
  const nodeStylesByPath = new Map<string, ResolvedNodeStyle>();
  const layoutHintsByPath = new Map<string, ResolvedLayoutHints>();
  const paintedByPath = new Map<string, PaintedNodeColors | undefined>();
  const resolvedStyleWarnings: ResolvedStyleWarning[] = [];

  /**
   * Record which frame colours the cascade named for `id` (#2662). Read the
   * same way `toResolvedNodeStyle` does, so "painted" means exactly "this
   * property was applied" rather than "this property appeared".
   */
  function paintedOf(props: Record<string, string>): PaintedNodeColors | undefined {
    const color = Boolean(props["color"]);
    const borderColor = Boolean(props["border-color"]);
    return color || borderColor ? { color, borderColor } : undefined;
  }
  function recordPaintedColors(id: string, painted: PaintedNodeColors | undefined): void {
    // Set or clear, never only set. `nodeStyles.set` overwrites unconditionally,
    // and an id can be stored twice (a deploy unit or an org node named like a
    // logical node). Leaving the first pass's flags standing against the second
    // pass's values is how the two maps come to disagree, which reads as the
    // frame keeping a colour nothing named for it.
    if (painted !== undefined) paintedColors.set(id, painted);
    else paintedColors.delete(id);
  }

  // Build inferred tag map so that dot-notation resource nodes (e.g. "OrderDB.OrderTable")
  // automatically match resource[table] / resource[queue] / resource[storage] selectors
  // even when no explicit tags are declared in the .krs source.
  const inferredTagMap = buildInferredTagMap(systems);

  // Recursively process nodes carrying down the nearest annotated ancestor's
  // annotations (`parentAnnotations`). When a node has no annotations of its
  // own, it renders as if it carried the parent's — see
  // ADR-517. Explicit annotations always
  // win, and a child with its own annotations becomes the new propagation
  // root for its own subtree.
  function processNodes(
    nodes: KrsNode[],
    parentAnnotations: string[],
    prefix: readonly string[],
  ): void {
    for (const node of nodes) {
      const path = [...prefix, node.id];
      const pathKey = nodePathRefId(path);
      // Inject inferred tag for dot-notation resource nodes with no explicit tags.
      let resolvedNode = node;
      if (
        node.kind === "resource" &&
        node.tags.length === 0 &&
        node.ref &&
        inferredTagMap.has(node.id)
      ) {
        resolvedNode = { ...node, tags: [inferredTagMap.get(node.id)!] };
      }
      // Apply annotation inheritance: empty own annotations fall back to the parent's.
      if (resolvedNode.annotations.length === 0 && parentAnnotations.length > 0) {
        resolvedNode = { ...resolvedNode, annotations: parentAnnotations };
      }
      const merged = mergeMatchingProperties(resolvedNode, allRules);
      const hints = finalizeLayoutHints(merged);
      // applyClientSubtypeFirstMatch mutates `merged` (shape-related only — does
      // not touch `column`), so it must run after the layout-hint read.
      applyClientSubtypeFirstMatch(resolvedNode, merged);
      const style = toResolvedNodeStyle(merged);
      const painted = paintedOf(merged);
      // Keyed by the node's own path; the simple-id entry is derived from these
      // after the walk (see `nodeStylesByPath`).
      nodeStylesByPath.set(pathKey, style);
      paintedByPath.set(pathKey, painted);
      // An entry for every node reached, empty when no hint resolved, so a card
      // can tell "no hints of its own" from "a path this pass never saw".
      layoutHintsByPath.set(pathKey, hints ?? {});
      // Also store under the annotation-qualified key so that two nodes sharing the same
      // ID but carrying different annotations (migration coexistence) each get their own
      // distinct style entry. A card that knows its path reads `nodesByPath`; the
      // qualified key serves the ones that do not (ghosts, stubs), ahead of the simple key.
      const qualifiedKey = nodeStyleKey(node.id, resolvedNode.annotations);
      if (qualifiedKey !== node.id) {
        nodeStyles.set(qualifiedKey, style);
        recordPaintedColors(qualifiedKey, painted);
      }
      // Inheritance only starts at `service`. A `system` carrying annotations
      // does not propagate them to its services (YAGNI).
      const downAnnotations =
        node.kind === "system"
          ? []
          : node.kind === "service"
            ? node.annotations
            : resolvedNode.annotations;
      // The synthesized "Unassigned" pseudo-system wraps top-level orphans,
      // whose full paths carry no system prefix (the layout stamps them so).
      const childPrefix = node.kind === "system" && node.id === UNASSIGNED_SYSTEM_ID ? [] : path;
      processNodes(node.children, downAnnotations, childPrefix);
    }
  }

  for (const system of systems) {
    processNodes([system], [], []);
    for (const edge of collectEdges(system)) {
      const style = resolveEdgeStyle(edge, allRules, cyclicTag);
      // Kind-qualified key (preferred by the renderer) so parallel sync/async
      // edges keep distinct styles; bare key as a fallback for synthetic edges.
      edgeStyles.set(edgeStyleKey(edge.from, edge.to, edge.kind), style);
      edgeStyles.set(`${edge.from}->${edge.to}`, style);
    }
  }

  if (extraNodes) {
    processNodes(extraNodes, [], []);
  }

  // The bare entries for the logical nodes: one node per shared id, the same one
  // in every map (#2934). Written before the deploy and org passes so those keep
  // overwriting a same-named logical entry, as they always did.
  const ranking = nodePathRanking ?? new Map<string, readonly (readonly string[])[]>();
  for (const [id, style] of bareIdView(nodeStylesByPath, ranking)) nodeStyles.set(id, style);
  for (const [id, painted] of bareIdView(paintedByPath, ranking)) recordPaintedColors(id, painted);
  for (const [id, hints] of bareIdView(layoutHintsByPath, ranking)) {
    if (Object.keys(hints).length > 0) layoutHints.set(id, hints);
  }

  if (extraEdges) addMissingEdgeStyles(edgeStyles, allRules, extraEdges, cyclicTag);

  if (deployNodes) {
    for (const unit of deployNodes) {
      const merged = mergeMatchingPropertiesForDeploy(unit, allRules);
      // Layout hints are surfaced only to warn that they have no effect here
      // (and so typo'd values still produce their invalid-value warning rather
      // than vanishing). The deploy view groups containers by `realizes`
      // target, so there is no container node a `grid-columns` hint could
      // attach to — the deploy grid auto-balances and is not author-overridable.
      const hint = finalizeLayoutHints(merged);
      if (hint?.column) {
        resolvedStyleWarnings.push({
          kind: "style-column-ignored-non-system-view",
          nodeId: unit.id,
          viewType: "deploy",
        });
      }
      nodeStyles.set(unit.id, toResolvedNodeStyle(merged));
      recordPaintedColors(unit.id, paintedOf(merged));
    }
  }

  if (organizations) {
    for (const node of collectOrgNodes(organizations)) {
      const merged = mergeMatchingPropertiesForOrg(node, allRules);
      const hint = finalizeLayoutHints(merged);
      if (hint?.column) {
        resolvedStyleWarnings.push({
          kind: "style-column-ignored-non-system-view",
          nodeId: node.id,
          viewType: "org",
        });
      }
      if (hint?.gridColumns !== undefined) {
        layoutHints.set(node.id, { gridColumns: hint.gridColumns });
      }
      nodeStyles.set(node.id, toResolvedNodeStyle(merged));
      recordPaintedColors(node.id, paintedOf(merged));
    }
  }

  return {
    nodes: nodeStyles,
    nodesByPath: nodeStylesByPath,
    edges: edgeStyles,
    boundaryFrames: resolveBoundaryFrames(allRules),
    teamFrames: resolveTeamFrames(allRules),
    paintedColors,
    paintedColorsByPath: new Map(
      [...paintedByPath].flatMap(([key, painted]) =>
        painted !== undefined ? [[key, painted] as const] : [],
      ),
    ),
    defaultNodeStyle: { ...DEFAULT_NODE_STYLE },
    defaultEdgeStyle: resolveDefaultEdgeStyle(allRules),
    layoutHints,
    layoutHintsByPath,
    warnings: resolvedStyleWarnings,
  };
}

/**
 * Read `column` and `grid-columns` from a merged property map and return the
 * resolved hint, or `null` when no hint resolved so the caller can skip
 * storing an empty map entry. Unknown hint properties are ignored to stay
 * forward-compatible.
 *
 * An invalid value never gets here: the cascade drops it and the validator
 * reports it once, where it was written (#3141). The checks below only narrow
 * the string for a rule built without value nodes.
 *
 * Callers are expected to share `merged` with the visual-style resolver so
 * the cascade runs once per node.
 */
function finalizeLayoutHints(merged: Record<string, string>): ResolvedLayoutHints | null {
  const hints: ResolvedLayoutHints = {};

  const rawColumn = merged["column"];
  if (rawColumn !== undefined && VALID_COLUMN_VALUES.has(rawColumn)) {
    hints.column = rawColumn as LayoutColumn;
  }

  const rawGrid = merged["grid-columns"];
  if (rawGrid !== undefined) {
    const n = Number(rawGrid);
    if (Number.isFinite(n)) hints.gridColumns = n;
  }

  return hints.column === undefined && hints.gridColumns === undefined ? null : hints;
}

// The cascade itself lives in style/cascade.ts, which the legend swatch
// resolver (`resolveLegendRefColor` in renderer/svg-builder.ts) calls too.
// These functions only decide *which* rules match; the order they are merged
// in is not theirs to redefine (Issue #1001, #2445).
function mergeMatchingProperties(node: KrsNode, rules: StyleRule[]): Record<string, string> {
  return mergeInCascadeOrder(rules.filter((rule) => nodeSelectorMatches(node, rule.selector)));
}

function mergeMatchingPropertiesForDeploy(
  unit: DeployNode,
  rules: StyleRule[],
): Record<string, string> {
  return mergeInCascadeOrder(
    rules.filter((rule) => deployNodeSelectorMatches(unit, rule.selector)),
  );
}

function mergeMatchingPropertiesForOrg(
  node: OrgNodeDescriptor,
  rules: StyleRule[],
): Record<string, string> {
  return mergeInCascadeOrder(rules.filter((rule) => orgNodeSelectorMatches(node, rule.selector)));
}

function orgNodeSelectorMatches(node: OrgNodeDescriptor, sel: StyleSelector): boolean {
  // An id narrows the match, it does not short-circuit it. `#<id>` on its own
  // still reaches whatever answers to that id, because nothing else in the
  // selector constrains it; `team#<id>` (#2269) additionally has to agree on the
  // kind. Returning early here would make the compound as wide as the bare form.
  if (sel.id && sel.id !== node.id) return false;
  if (sel.nodeType === "edge") return false;
  if (sel.nodeType && sel.nodeType !== node.kind) return false;
  // Org nodes carry no tags, so tag selectors never match. Same for `facets`:
  // the property is on logical nodes, not on `team` / `member`.
  if (sel.tags.length > 0) return false;
  if (sel.facets.length > 0) return false;
  // Annotation selectors (e.g. the default-style `@migration_target` / `@deprecated`
  // badge rules) match a team carrying every required annotation — same semantics
  // as nodeSelectorMatches, so org teams get migration/deprecation badges too (#1583).
  if (sel.annotations.length > 0) {
    if (!sel.annotations.every((a) => node.annotations.includes(a))) return false;
  }
  if (!sel.nodeType && !sel.id && sel.annotations.length === 0) return false;
  return true;
}

function resolveDefaultEdgeStyle(rules: StyleRule[]): ResolvedEdgeStyle {
  const matching = rules.filter(
    (rule) => rule.selector.nodeType === "edge" && rule.selector.tags.length === 0,
  );
  return toResolvedEdgeStyle(mergeInCascadeOrder(matching));
}

/**
 * Boundary frame styles from `boundary` / `boundary#<id>` rules (#2234).
 *
 * Built from the *rules* rather than from the model's boundary declarations, so
 * `resolveStyles` needs no new input. The base / by-id split and the cascade are
 * {@link resolveFrames}, shared with the team axis.
 */
function resolveBoundaryFrames(rules: StyleRule[]): ResolvedBoundaryFrames {
  return resolveFrames(
    rules.filter((rule) => rule.selector.nodeType === "boundary"),
    (selector) => selector.boundaryId,
  );
}

/**
 * The base / by-id split both frame axes resolve into, given the rules that
 * apply to an axis and how that axis reads an id off a selector.
 *
 * Shared because the two differ in exactly those two inputs. The cascade is the
 * common one (specificity, then declaration order), so a bare rule loses to a
 * named one without this function knowing the scores, and a rule naming
 * something that does not exist is simply never looked up — the way
 * `#NoSuchNode` already behaves.
 */
function resolveFrames(
  rules: StyleRule[],
  idOf: (selector: StyleSelector) => string | undefined,
): { base: ResolvedFrameStyle; byId: Map<string, ResolvedFrameStyle> } {
  const merge = (matching: StyleRule[]): ResolvedFrameStyle =>
    toResolvedFrameStyle(mergeInCascadeOrder(matching));
  const base = merge(rules.filter((rule) => idOf(rule.selector) === undefined));
  const byId = new Map<string, ResolvedFrameStyle>();
  const namedIds = new Set(
    rules.map((rule) => idOf(rule.selector)).filter((id): id is string => id !== undefined),
  );
  for (const id of namedIds) {
    byId.set(
      id,
      merge(
        rules.filter((rule) => {
          const ruleId = idOf(rule.selector);
          return ruleId === undefined || ruleId === id;
        }),
      ),
    );
  }
  return { base, byId };
}

/**
 * Team frame styles from `team` / `#<id>` / `team#<id>` rules (#2269).
 *
 * A team is one entity with two renderings — the card in the org tree view and
 * the frame the system view draws under *Group by: team* — so one selector
 * addresses both, and this is the frame's half of the answer.
 *
 * **Author sheets only, and that is the whole reason this function exists.** The
 * built-in sheet already declares `team { background-color: #D1FAE5;
 * border-color: #6EE7B7; … }`, which describes the *card*: it predates the frame
 * and is the card's default, not the team's colour. Reading it here would repaint
 * every team frame green the moment this feature shipped, against the monochrome
 * team frames #2179 decided on and against "teams the sheet does not name are
 * unchanged". The two renderings therefore keep separate defaults — the card's
 * in the built-in sheet, the frame's in the renderer — and only what an author
 * declared crosses between them.
 *
 * `boundary` needed no such filter (#2234) only because the built-in sheet has
 * no `boundary` rule to leak; the stance is the same on both axes. The filter is
 * per rule rather than per sheet because `StyleRule.sheetId` is where a rule's
 * origin is canonical — the `StyleSheet` envelope's copy is optional.
 *
 * Otherwise the same shape as {@link resolveBoundaryFrames}: built from the
 * rules, so a rule naming a team that does not exist is simply never looked up,
 * and the shared cascade sorts `team` (1) under `#<id>` / `team#<id>` (100 /
 * 101) without this function knowing the scores. Selectors carrying tags,
 * annotations or facets are left out: org nodes carry no tags or facets, and an
 * annotation rule is a badge rule, which a frame does not draw.
 */
function resolveTeamFrames(allRules: StyleRule[]): ResolvedTeamFrames {
  return resolveFrames(
    allRules.filter((rule) => {
      if (NON_AUTHOR_SHEET_IDS.has(rule.sheetId)) return false;
      const sel = rule.selector;
      // Predicates a team frame cannot answer. Org nodes carry no tags or
      // facets; an annotation rule is a badge rule, which a frame does not
      // draw; and the edge / boundary parts name other id spaces entirely.
      // Letting any of them through would drop the predicate rather than honour
      // it, so `team[from=X]` would silently widen to *every* frame.
      if (sel.tags.length > 0 || sel.annotations.length > 0 || sel.facets.length > 0) return false;
      if (sel.edgeFrom !== undefined || sel.edgeTo !== undefined) return false;
      if (sel.edgeId !== undefined || sel.boundaryId !== undefined) return false;
      // `team` (bare or compound) and a plain `#<id>`. A bare id selector carries
      // no kind, so it reaches whichever entity answers to that id — exactly what
      // it already does for the card.
      return sel.nodeType === "team" || (sel.nodeType === undefined && sel.id !== undefined);
    }),
    (selector) => selector.id,
  );
}

/**
 * The frame-relevant subset of the property list. Anything outside it is
 * ignored rather than half-applied, matching how the Org Tree View documents
 * its own subset (`docs/spec/style.md`).
 */
function toResolvedFrameStyle(props: Record<string, string>): ResolvedFrameStyle {
  const style: ResolvedFrameStyle = {};
  if (props["border-color"]) style.borderColor = props["border-color"];
  if (props["background-color"]) style.backgroundColor = props["background-color"];
  if (props["color"]) style.color = props["color"];
  // A non-numeric value would otherwise reach the SVG as `stroke-width="NaN"`,
  // which renderers and the drawio exporter disagree about.
  const width = finiteNumber(props["border-width"]);
  if (width !== undefined) style.borderWidth = width;
  if (props["border-style"]) {
    style.borderStyle = props["border-style"] as "solid" | "dashed" | "dotted";
  }
  return style;
}

function collectEdges(node: KrsNode): KrsEdge[] {
  const edges: KrsEdge[] = [...node.edges];
  for (const child of node.children) {
    edges.push(...collectEdges(child));
  }
  return edges;
}

/**
 * No-op for everything except multi-subtype client nodes.
 *
 * When a client node has more than one of {@link CLIENT_SUBTYPE_TAGS}, CSS
 * cascade alone would resolve to the rule declared latest in the icon theme;
 * that contradicts the intuition that the *first* tag the user wrote should
 * win. This helper rewrites the resolved shape so `client X [mobile] [desktop]`
 * picks `client-mobile`. Single-tag and zero-tag clients are handled correctly
 * by the cascade and untouched here. User-defined shape overrides (anything
 * that is not a `client-<subtype>` icon URL) are left alone too.
 */
const CLIENT_SUBTYPE_SHAPE_RE = new RegExp(
  String.raw`^url\("client-(?:${CLIENT_SUBTYPE_TAGS.join("|")})"\)$`,
);

function applyClientSubtypeFirstMatch(node: KrsNode, merged: Record<string, string>): void {
  if (node.kind !== "client") return;
  const subtypes: ClientSubtypeTag[] = node.tags.filter((t): t is ClientSubtypeTag =>
    (CLIENT_SUBTYPE_TAGS as readonly string[]).includes(t),
  );
  if (subtypes.length < 2) return;
  const currentShape = merged["shape"];
  if (!currentShape || !CLIENT_SUBTYPE_SHAPE_RE.test(currentShape)) return;
  merged["shape"] = `url("client-${subtypes[0]}")`;
}

function resolveEdgeStyle(edge: KrsEdge, rules: StyleRule[], cyclicTag = true): ResolvedEdgeStyle {
  const matching = rules.filter((rule) => edgeSelectorMatches(edge, rule.selector, cyclicTag));
  return toResolvedEdgeStyle(mergeInCascadeOrder(matching));
}

function deployNodeSelectorMatches(unit: DeployNode, sel: StyleSelector): boolean {
  // Narrowing, not short-circuiting — see `orgNodeSelectorMatches`.
  if (sel.id && sel.id !== unit.id) return false;
  if (sel.nodeType === "edge") return false;
  if (sel.nodeType && sel.nodeType !== unit.kind) return false;
  if (sel.tags.length > 0) return false;
  if (sel.annotations.length > 0) return false;
  // Deploy units are not logical nodes and carry no `facets` property.
  if (sel.facets.length > 0) return false;
  if (!sel.nodeType && !sel.id) return false;
  return true;
}

interface OrgNodeDescriptor {
  id: string;
  kind: "team" | "member";
  annotations: string[];
}

function collectOrgNodes(organizations: OrganizationBlock[]): OrgNodeDescriptor[] {
  const nodes: OrgNodeDescriptor[] = [];

  function walk(team: TeamNode): void {
    nodes.push({ id: team.id, kind: "team", annotations: team.annotations });
    for (const child of team.children) {
      if (child.kind === "member") {
        nodes.push({ id: child.id, kind: "member", annotations: [] });
      } else {
        walk(child);
      }
    }
  }

  for (const org of organizations) {
    for (const team of org.teams) {
      walk(team);
    }
  }

  return nodes;
}

function nodeSelectorMatches(node: KrsNode, selector: StyleSelector): boolean {
  // Narrowing, not short-circuiting — see `orgNodeSelectorMatches`. A bare
  // `#<id>` carries nothing else (the parser returns as soon as it reads one),
  // so it falls through every remaining test unchanged.
  if (selector.id && node.id !== selector.id) return false;
  if (selector.nodeType === "edge") return false;
  if (selector.nodeType && selector.nodeType !== node.kind) return false;
  if (selector.tags.length > 0) {
    if (!selector.tags.every((t) => node.tags.includes(t))) return false;
  }
  if (selector.annotations.length > 0) {
    if (!selector.annotations.every((a) => node.annotations.includes(a))) return false;
  }
  // `[facets=<id>]` (#2175). Read straight off the node: membership is written
  // element-side, so no facet index has to reach the style resolver. ANDed like
  // tags — `[facets=pii][facets=gdpr]` wants both.
  if (selector.facets.length > 0) {
    const facets = node.facets;
    if (!facets) return false;
    if (!selector.facets.every((f) => facets.includes(f))) return false;
  }
  // A bare selector with no type, tags, annotations, facets, or id shouldn't
  // match anything.
  if (
    !selector.nodeType &&
    selector.tags.length === 0 &&
    selector.annotations.length === 0 &&
    selector.facets.length === 0 &&
    !selector.id
  ) {
    return false;
  }
  return true;
}

function edgeSelectorMatches(edge: KrsEdge, selector: StyleSelector, cyclicTag: boolean): boolean {
  // Edge selectors require the explicit `edge` type; tag-only selectors
  // (nodeType === undefined) never match edges.
  if (selector.nodeType !== "edge") return false;
  // `[facets=<id>]` on an edge (#2544), read the same way as on a node: straight
  // off the element that declares the membership, and ANDed when repeated —
  // `edge[facets=pii][facets=gdpr]` wants both.
  if (selector.facets.length > 0) {
    const facets = edge.facets;
    if (!facets) return false;
    if (!selector.facets.every((f) => facets.includes(f))) return false;
  }
  if (selector.edgeId !== undefined) {
    if (edge.canonicalId === undefined) return false;
    if (edge.canonicalId !== selector.edgeId) return false;
  }
  // `edge[from=<id>]` / `edge[to=<id>]` match by endpoint id. `edge.from` /
  // `edge.to` hold the same id form (bare, or dot-notation for synthesized
  // usecase→resource edges) that the canonical base id is built from, so the
  // comparison stays consistent with `edge#<from>-><to>`.
  if (selector.edgeFrom !== undefined && edge.from !== selector.edgeFrom) return false;
  if (selector.edgeTo !== undefined && edge.to !== selector.edgeTo) return false;
  if (selector.tags.length > 0) {
    const edgeTags = [...edge.tags];
    if (edge.kind === "async") edgeTags.push("async");
    if (edge.kind === "sync") edgeTags.push("sync");
    if (cyclicTag && edge.cyclic) edgeTags.push("cyclic");
    if (!selector.tags.every((t) => edgeTags.includes(t))) return false;
  }
  return true;
}

/**
 * A number, read from the start of the value so a length's unit falls away
 * (the builtin theme writes lengths without one). Bounds are not checked
 * here: the cascade already dropped an author value outside them, and the
 * validator reported it (#3141). A value that is not a finite number is still
 * dropped, so a hand-built rule cannot reach the SVG as `NaN`.
 */
function finiteNumber(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}

function toResolvedNodeStyle(props: Record<string, string>): ResolvedNodeStyle {
  const style = { ...DEFAULT_NODE_STYLE };

  if (props["background-color"]) style.backgroundColor = props["background-color"];
  if (props["color"]) style.color = props["color"];
  if (props["border-color"]) style.borderColor = props["border-color"];
  const borderWidth = finiteNumber(props["border-width"]);
  if (borderWidth !== undefined) style.borderWidth = borderWidth;
  if (props["border-style"])
    style.borderStyle = props["border-style"] as "solid" | "dashed" | "dotted";
  const borderRadius = finiteNumber(props["border-radius"]);
  if (borderRadius !== undefined) style.borderRadius = borderRadius;
  const fontSize = finiteNumber(props["font-size"]);
  if (fontSize !== undefined) style.fontSize = fontSize;
  if (props["font-weight"]) style.fontWeight = props["font-weight"] as "normal" | "bold";
  if (props["font-family"]) style.fontFamily = props["font-family"];
  const opacity = finiteNumber(props["opacity"]);
  if (opacity !== undefined) style.opacity = opacity;
  if (props["shape"]) {
    const shapeVal = props["shape"];
    if (shapeVal.startsWith("url(")) {
      const match = shapeVal.match(/url\("(.+?)"\)/);
      style.shape = match ? { url: match[1] } : "box";
    } else if (SHAPE_KEYWORDS.has(shapeVal)) {
      style.shape = shapeVal as ShapeKind;
    } else if (hasShape(shapeVal)) {
      style.shape = { url: shapeVal };
    }
  }
  if (props["badge-color"]) style.badgeColor = props["badge-color"];
  if (props["badge-icon"]) style.badgeIcon = stripQuotes(props["badge-icon"]);
  if (props["badge-label"]) style.badgeLabel = stripQuotes(props["badge-label"]);

  return style;
}

const EDGE_STROKE_STYLES = new Set<ResolvedEdgeStyle["strokeStyle"]>(["solid", "dashed", "dotted"]);

function toResolvedEdgeStyle(props: Record<string, string>): ResolvedEdgeStyle {
  const style = { ...DEFAULT_EDGE_STYLE };

  if (props["color"]) style.color = props["color"];
  const strokeWidth = finiteNumber(props["stroke-width"]);
  if (strokeWidth !== undefined) style.strokeWidth = strokeWidth;
  const fontSize = finiteNumber(props["font-size"]);
  if (fontSize !== undefined) style.fontSize = fontSize;
  if (props["border-style"]) {
    const value = props["border-style"] as ResolvedEdgeStyle["strokeStyle"];
    if (EDGE_STROKE_STYLES.has(value)) style.strokeStyle = value;
  }
  // `stroke-style` is the canonical edge-side name (ADR-1492);
  // `border-style` stays supported for back-compat. When both are
  // declared, the canonical name wins regardless of declaration order.
  if (props["stroke-style"]) {
    const value = props["stroke-style"] as ResolvedEdgeStyle["strokeStyle"];
    if (EDGE_STROKE_STYLES.has(value)) style.strokeStyle = value;
  }
  if (props["direction"]) {
    const value = props["direction"];
    if (EDGE_DIRECTION_VALUES.has(value)) {
      style.direction = value as ResolvedEdgeStyle["direction"];
    }
  }
  if (props["label-position"]) {
    const raw = props["label-position"];
    if (raw in LABEL_POSITION_KEYWORDS) {
      style.labelPosition = LABEL_POSITION_KEYWORDS[raw];
    } else {
      const numeric = finiteNumber(raw);
      if (numeric !== undefined) style.labelPosition = numeric;
    }
  }
  if (props["label-offset"]) {
    // CSS-like shorthand:
    //   "8px"       → x=0, y=8
    //   "4px 8px"   → x=4, y=8
    // The two-token form mirrors `padding`/`margin` and lets a global
    // rule like `edge { label-offset: 0 8px; }` shift every label by
    // the same screen-axis amount, regardless of edge slope.
    const tokens = props["label-offset"].trim().split(/\s+/);
    if (tokens.length === 1) {
      const dy = parseFloat(tokens[0]);
      if (Number.isFinite(dy)) {
        style.labelOffsetX = 0;
        style.labelOffsetY = dy;
      }
    } else if (tokens.length === 2) {
      const dx = parseFloat(tokens[0]);
      const dy = parseFloat(tokens[1]);
      if (Number.isFinite(dx) && Number.isFinite(dy)) {
        style.labelOffsetX = dx;
        style.labelOffsetY = dy;
      }
    }
  }
  // The canvas tier of edge-label disclosure (#3022).
  if (props["label-max-chars"]) {
    const raw = props["label-max-chars"].trim();
    if (raw === "none") {
      style.labelMaxChars = Infinity;
    } else {
      const n = Number(raw);
      if (Number.isFinite(n)) style.labelMaxChars = n;
    }
  }
  if (props["label-display"]) {
    const raw = props["label-display"].trim();
    if (raw === "always" || raw === "auto" || raw === "hover") style.labelDisplay = raw;
  }

  return style;
}

function stripQuotes(s: string): string {
  if (s.startsWith('"') && s.endsWith('"')) return s.slice(1, -1);
  return s;
}

/**
 * Build the style-map key for a node.
 * When the node has annotations, append them (sorted) to the ID so that
 * nodes with the same ID but different annotations (migration coexistence)
 * each get their own style entry and do not clobber one another.
 *
 * Examples:
 *   nodeStyleKey("Contract", [])                    → "Contract"
 *   nodeStyleKey("Contract", ["deprecated"])         → "Contract@deprecated"
 *   nodeStyleKey("Contract", ["migration_target"])   → "Contract@migration_target"
 */
export function nodeStyleKey(id: string, annotations: readonly string[] | undefined): string {
  if (!annotations || annotations.length === 0) return id;
  return `${id}@${[...annotations].sort().join(",")}`;
}

/**
 * Style edges that are not in the model — the ones a view derives at
 * extraction time (implicit service edges, entity relations projected onto a
 * store canvas) — without disturbing an authored edge's entry: only a key the
 * map lacks is added, so a `.krs` edge on the same pair keeps its own style.
 */
function addMissingEdgeStyles(
  edgeStyles: Map<string, ResolvedEdgeStyle>,
  rules: StyleRule[],
  edges: readonly KrsEdge[],
  cyclicTag = true,
): void {
  for (const edge of edges) {
    const qualifiedKey = edgeStyleKey(edge.from, edge.to, edge.kind);
    if (edgeStyles.has(qualifiedKey)) continue;
    const style = resolveEdgeStyle(edge, rules, cyclicTag);
    edgeStyles.set(qualifiedKey, style);
    if (!edgeStyles.has(`${edge.from}->${edge.to}`)) {
      edgeStyles.set(`${edge.from}->${edge.to}`, style);
    }
  }
}

const flattenedRulesCache = new WeakMap<StyleSheet[], StyleRule[]>();

/**
 * Give a view slice's derived edges a style entry after `resolveStyles` ran.
 *
 * The compile path hands the slice's edges to `resolveStyles` as `extraEdges`
 * up front. The static bundles cannot: they resolve styles once and then
 * extract one slice per drill-down page, so an edge that exists only on a page
 * — `[implicit]` service edges, `[projected]` store relations (#2721) — fell
 * back to `defaultEdgeStyle` there, losing its colour and even its `[async]`
 * dash. Calling this on each page's slice closes that gap (TPL-219: the static
 * bundle shows what the app shows). Returns the slice for use inline.
 */
export function styleDerivedEdges<T extends { childEdges: KrsEdge[] }>(
  slice: T,
  styles: ResolvedStyles,
  sheets: StyleSheet[],
): T {
  let rules = flattenedRulesCache.get(sheets);
  if (!rules) {
    rules = flattenSheetsInCascadeOrder(sheets);
    flattenedRulesCache.set(sheets, rules);
  }
  addMissingEdgeStyles(styles.edges, rules, slice.childEdges);
  return slice;
}

/**
 * Build the style-map key for an edge.
 *
 * Parallel edges between the same `(from, to)` pair (e.g. a sync `A -> B` and an
 * async `A --> B`) must each keep their own resolved style — `kind` drives the
 * stroke style, so keying the map by `from->to` alone makes the last edge clobber
 * the others. Appending the kind disambiguates them. Synthetic layout-only edges
 * (delivers, owns, ghosts, aggregated domain edges) pass `kind === undefined` and
 * fall back to the bare `from->to` key, which the resolver also registers.
 *
 * Examples:
 *   edgeStyleKey("A", "B", undefined) → "A->B"
 *   edgeStyleKey("A", "B", "sync")    → "A->B#sync"
 *   edgeStyleKey("A", "B", "async")   → "A->B#async"
 */
export function edgeStyleKey(from: string, to: string, kind: EdgeKind | undefined): string {
  return kind === undefined ? `${from}->${to}` : `${from}->${to}#${kind}`;
}
